<template>
  <div class="period-picker d-flex flex-wrap align-items-center" style="gap: 6px;">
    <div v-if="withGranularity" class="btn-group btn-group-sm" role="group" aria-label="Group by">
      <button v-for="g in granularities" :key="g.key" type="button" class="btn"
        :class="value.granularity === g.key ? 'btn-primary' : 'btn-outline-primary'" @click="setGranularity(g.key)">{{ g.label }}</button>
    </div>
    <select class="form-control form-control-sm" style="width: auto;" v-model="preset" @change="applyPreset" aria-label="Period">
      <option v-for="p in presets" :key="p.key" :value="p.key">{{ p.label }}</option>
      <option value="custom">Custom dates</option>
    </select>
    <template v-if="preset === 'custom'">
      <input type="date" class="form-control form-control-sm" style="width: auto;" v-model="from" aria-label="From" />
      <input type="date" class="form-control form-control-sm" style="width: auto;" v-model="to" aria-label="To" />
      <button type="button" class="btn btn-sm btn-primary" @click="emitChange">Apply</button>
    </template>
  </div>
</template>

<script>
import moment from "moment";

/**
 * Period picker for the analytics charts. v-model: { from, to, granularity }.
 * Presets fit the granularity (12 weeks, 12 months, 5 years); custom dates are applied on Apply.
 */
export default {
  name: "PeriodPicker",
  props: {
    value: { type: Object, required: true },
    withGranularity: { type: Boolean, default: false },
    defaultPreset: { type: String, default: "12m" },
  },
  data() {
    return {
      preset: this.defaultPreset,
      from: this.value.from,
      to: this.value.to,
      granularities: [{ key: "week", label: "Weekly" }, { key: "month", label: "Monthly" }, { key: "year", label: "Yearly" }],
    };
  },
  computed: {
    presets() {
      return [
        { key: "30d", label: "Last 30 days" },
        { key: "12w", label: "Last 12 weeks" },
        { key: "90d", label: "Last 3 months" },
        { key: "12m", label: "Last 12 months" },
        { key: "ytd", label: "This year" },
        { key: "lastyear", label: "Last year" },
        { key: "5y", label: "Last 5 years" },
      ];
    },
  },
  created() {
    if (!this.value.from) this.applyPreset();
  },
  methods: {
    range(key) {
      const today = moment();
      switch (key) {
        case "30d": return [today.clone().subtract(29, "days"), today];
        case "12w": return [today.clone().subtract(11, "weeks").startOf("isoWeek"), today];
        case "90d": return [today.clone().subtract(3, "months").add(1, "day"), today];
        case "ytd": return [today.clone().startOf("year"), today];
        case "lastyear": return [today.clone().subtract(1, "year").startOf("year"), today.clone().subtract(1, "year").endOf("year")];
        case "5y": return [today.clone().subtract(4, "years").startOf("year"), today];
        default: return [today.clone().subtract(11, "months").startOf("month"), today];
      }
    },
    applyPreset() {
      if (this.preset === "custom") return;
      const [from, to] = this.range(this.preset);
      this.from = from.format("YYYY-MM-DD");
      this.to = to.format("YYYY-MM-DD");
      this.emitChange();
    },
    setGranularity(g) {
      // Pick a sensible window for the new grouping.
      const preset = { week: "12w", month: "12m", year: "5y" }[g];
      if (this.preset !== "custom") this.preset = preset;
      const [from, to] = this.preset === "custom" ? [moment(this.from), moment(this.to)] : this.range(preset);
      this.from = from.format("YYYY-MM-DD");
      this.to = to.format("YYYY-MM-DD");
      this.$emit("input", { ...this.value, from: this.from, to: this.to, granularity: g });
      this.$emit("change");
    },
    emitChange() {
      this.$emit("input", { ...this.value, from: this.from, to: this.to });
      this.$emit("change");
    },
  },
};
</script>
